import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api, resolveImageUrl } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Users, MapPin, Briefcase, Lock, Globe, ArrowLeft, Heart, MessageCircle,
  Send, Settings, UserPlus, Check, Crown, ChevronDown, ChevronUp, Calendar, Clock, Video, Building2, Plus, X
} from 'lucide-react';
import { toast } from 'sonner';

const timeAgo = (date) => {
  const s = Math.floor((Date.now() - new Date(date)) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const PostCard = ({ post, onLike, onComment, groupId }) => {
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleComment = async () => {
    if (!commentText.trim()) return;
    setSubmitting(true);
    await onComment(post.id, commentText.trim());
    setCommentText('');
    setSubmitting(false);
    setShowComments(true);
  };

  return (
    <Card className="overflow-hidden" data-testid={`post-${post.id}`}>
      <CardContent className="p-5">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
            {post.user_photo ? (
              <img src={resolveImageUrl(post.user_photo)} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-sm font-bold text-primary">{(post.user_name || '?')[0]}</span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm">{post.user_name}</p>
            <p className="text-[11px] text-muted-foreground">{post.user_profession} · {timeAgo(post.created_at)}</p>
          </div>
        </div>
        <p className="text-sm leading-relaxed mb-4 whitespace-pre-wrap">{post.content}</p>
        <div className="flex items-center gap-4 pt-3 border-t border-border/50">
          <button
            onClick={() => onLike(post.id)}
            className={`flex items-center gap-1.5 text-xs transition-colors ${post.liked ? 'text-red-500' : 'text-muted-foreground hover:text-red-400'}`}
            data-testid={`like-post-${post.id}`}
          >
            <Heart className={`w-4 h-4 ${post.liked ? 'fill-current' : ''}`} />
            <span>{post.like_count || 0}</span>
          </button>
          <button
            onClick={() => setShowComments(!showComments)}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
            data-testid={`toggle-comments-${post.id}`}
          >
            <MessageCircle className="w-4 h-4" />
            <span>{post.comment_count || 0}</span>
            {showComments ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        </div>
        {showComments && (
          <div className="mt-3 pt-3 border-t border-border/30 space-y-3">
            {(post.comments || []).map(c => (
              <div key={c.id} className="flex items-start gap-2.5">
                <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {c.user_photo ? (
                    <img src={resolveImageUrl(c.user_photo)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-[10px] font-bold text-muted-foreground">{(c.user_name || '?')[0]}</span>
                  )}
                </div>
                <div className="flex-1 bg-muted/50 rounded-lg px-3 py-2">
                  <p className="text-xs font-semibold">{c.user_name} <span className="font-normal text-muted-foreground">· {timeAgo(c.created_at)}</span></p>
                  <p className="text-xs mt-0.5">{c.content}</p>
                </div>
              </div>
            ))}
            <div className="flex gap-2">
              <Input
                value={commentText}
                onChange={e => setCommentText(e.target.value)}
                placeholder="Write a comment..."
                className="text-xs h-8"
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleComment()}
                data-testid={`comment-input-${post.id}`}
              />
              <Button size="sm" className="h-8 px-3" disabled={submitting || !commentText.trim()} onClick={handleComment} data-testid={`submit-comment-${post.id}`}>
                <Send className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function GroupDetailPage() {
  const { groupId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [posts, setPosts] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPost, setNewPost] = useState('');
  const [posting, setPosting] = useState(false);
  const [groupEvents, setGroupEvents] = useState([]);
  const [showCreateEvent, setShowCreateEvent] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [groupData, postsData, membersData, eventsData] = await Promise.all([
        api.getGroup(groupId),
        api.getGroupPosts(groupId),
        api.getGroupMembers(groupId),
        api.getGroupEvents(groupId),
      ]);
      setGroup(groupData);
      setPosts(postsData.posts || []);
      setMembers(membersData.members || []);
      setGroupEvents(eventsData.events || []);
    } catch (err) {
      toast.error('Failed to load group');
      navigate('/groups');
    } finally { setLoading(false); }
  }, [groupId, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreatePost = async () => {
    if (!newPost.trim()) return;
    setPosting(true);
    try {
      const post = await api.createGroupPost(groupId, newPost.trim());
      setPosts([post, ...posts]);
      setNewPost('');
      toast.success('Post created!');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to post');
    } finally { setPosting(false); }
  };

  const handleLike = async (postId) => {
    try {
      const result = await api.likeGroupPost(groupId, postId);
      setPosts(posts.map(p => p.id === postId ? {
        ...p, liked: result.liked, like_count: result.liked ? (p.like_count || 0) + 1 : Math.max(0, (p.like_count || 0) - 1)
      } : p));
    } catch { toast.error('Failed'); }
  };

  const handleComment = async (postId, content) => {
    try {
      const comment = await api.commentGroupPost(groupId, postId, content);
      setPosts(posts.map(p => p.id === postId ? {
        ...p, comments: [...(p.comments || []), comment], comment_count: (p.comment_count || 0) + 1
      } : p));
    } catch { toast.error('Failed to comment'); }
  };

  const handleJoin = async () => {
    try {
      const result = await api.joinGroup(groupId);
      if (result.pending) {
        toast.info(result.message);
      } else {
        toast.success(result.joined ? 'Joined!' : 'Left group');
        fetchData();
      }
    } catch (err) { toast.error('Failed'); }
  };

  const TypeIcon = group?.group_type === 'location' ? MapPin
    : group?.group_type === 'industry' ? Briefcase
    : Users;

  if (loading) {
    return (
      <Layout>
        <div className="animate-pulse space-y-4">
          <div className="h-56 bg-muted rounded-xl" />
          <div className="h-8 bg-muted rounded w-1/3" />
          <div className="h-32 bg-muted rounded-xl" />
        </div>
      </Layout>
    );
  }

  if (!group) return null;

  const isMember = group.is_member;
  const isCreator = group.is_creator;
  const creator = members.find(m => m.role === 'admin');

  return (
    <Layout>
      <div className="space-y-6" data-testid="group-detail-page">
        {/* Back button */}
        <Button variant="ghost" size="sm" onClick={() => navigate('/groups')} className="gap-1.5" data-testid="back-to-groups">
          <ArrowLeft className="w-4 h-4" /> Back to Groups
        </Button>

        {/* Cover Header */}
        <div className="rounded-xl overflow-hidden relative">
          <div className="h-56 relative">
            {(group.cover_image || group.image_url) ? (
              <img src={resolveImageUrl(group.cover_image || group.image_url)} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 flex items-center justify-center">
                <TypeIcon className="w-20 h-20 text-white/10" />
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
            <div className="absolute bottom-5 left-6 right-6">
              <div className="flex items-center gap-2 mb-2">
                {group.is_private ? (
                  <Badge className="bg-amber-500/80 text-white text-[10px] border-0"><Lock className="w-2.5 h-2.5 mr-1" />Private</Badge>
                ) : (
                  <Badge className="bg-emerald-500/80 text-white text-[10px] border-0"><Globe className="w-2.5 h-2.5 mr-1" />Public</Badge>
                )}
                <Badge variant="outline" className="bg-white/15 text-white text-[10px] border-white/20 capitalize">
                  <TypeIcon className="w-2.5 h-2.5 mr-1" />{group.group_type}
                </Badge>
              </div>
              <h1 className="text-3xl font-bold text-white drop-shadow-lg">{group.name}</h1>
              <div className="flex items-center gap-4 mt-2">
                <span className="text-sm text-white/80 flex items-center gap-1.5"><Users className="w-4 h-4" />{group.member_count || members.length} members</span>
                {group.location && <span className="text-sm text-white/60 flex items-center gap-1.5"><MapPin className="w-4 h-4" />{group.location}</span>}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-5">
            {/* About */}
            <Card>
              <CardContent className="p-5">
                <h3 className="font-semibold text-sm mb-2">About this group</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {group.description || 'A community for professionals to connect, share insights, and grow together.'}
                </p>
              </CardContent>
            </Card>

            {/* Post Composer */}
            {isMember ? (
              <Card data-testid="post-composer">
                <CardContent className="p-5">
                  <div className="flex gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {user?.profile_photo_url ? (
                        <img src={resolveImageUrl(user.profile_photo_url)} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-sm font-bold text-primary">{(user?.full_name || 'U')[0]}</span>
                      )}
                    </div>
                    <div className="flex-1">
                      <textarea
                        value={newPost}
                        onChange={e => setNewPost(e.target.value)}
                        placeholder="Share something with the group..."
                        className="w-full px-3 py-2.5 text-sm rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                        rows={3}
                        data-testid="group-post-input"
                      />
                      <div className="flex justify-end mt-2">
                        <Button size="sm" disabled={posting || !newPost.trim()} onClick={handleCreatePost} data-testid="submit-group-post">
                          <Send className="w-3.5 h-3.5 mr-1.5" />{posting ? 'Posting...' : 'Post'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-dashed">
                <CardContent className="py-8 text-center">
                  <Users className="w-8 h-8 mx-auto text-muted-foreground/40 mb-2" />
                  <p className="text-sm text-muted-foreground mb-3">Join this group to participate in discussions</p>
                  <Button size="sm" onClick={handleJoin} data-testid="join-to-post">
                    <UserPlus className="w-4 h-4 mr-1.5" />Join Group
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Posts Feed */}
            <div className="space-y-4" data-testid="group-posts-feed">
              {posts.length > 0 ? posts.map(post => (
                <PostCard key={post.id} post={post} onLike={handleLike} onComment={handleComment} groupId={groupId} />
              )) : (
                <Card>
                  <CardContent className="py-10 text-center">
                    <MessageCircle className="w-8 h-8 mx-auto text-muted-foreground/30 mb-2" />
                    <p className="text-sm text-muted-foreground">No posts yet. {isMember ? 'Be the first to share!' : 'Join to start discussions.'}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-5">
            {/* Join / Manage Action */}
            <Card>
              <CardContent className="p-5">
                {isCreator ? (
                  <Button className="w-full" onClick={() => navigate('/groups')} data-testid="manage-group-sidebar">
                    <Settings className="w-4 h-4 mr-2" />Manage Group
                  </Button>
                ) : isMember ? (
                  <Button variant="outline" className="w-full text-green-600 border-green-600" onClick={handleJoin} data-testid="leave-group-sidebar">
                    <Check className="w-4 h-4 mr-2" />Joined · Leave
                  </Button>
                ) : (
                  <Button className="w-full" onClick={handleJoin} data-testid="join-group-sidebar">
                    <UserPlus className="w-4 h-4 mr-2" />Join Group
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Group Leader / Creator */}
            {creator && (
              <Card data-testid="group-leader-card">
                <CardContent className="p-5">
                  <h3 className="font-semibold text-sm mb-3 flex items-center gap-1.5">
                    <Crown className="w-4 h-4 text-amber-500" /> Group Leader
                  </h3>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                      {creator.photo ? (
                        <img src={resolveImageUrl(creator.photo)} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-lg font-bold text-primary">{(creator.name || '?')[0]}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate">{creator.name}</p>
                      <p className="text-[11px] text-muted-foreground">Group Creator</p>
                    </div>
                  </div>
                  {!isCreator && (
                    <Link to="/network">
                      <Button variant="outline" size="sm" className="w-full text-xs" data-testid="connect-leader">
                        <UserPlus className="w-3.5 h-3.5 mr-1.5" />Connect with {creator.name?.split(' ')[0]}
                      </Button>
                    </Link>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Members */}
            <Card data-testid="group-members-sidebar">
              <CardContent className="p-5">
                <h3 className="font-semibold text-sm mb-3 flex items-center gap-1.5">
                  <Users className="w-4 h-4 text-primary" /> Members ({members.length})
                </h3>
                <div className="space-y-2.5">
                  {members.slice(0, 10).map(m => (
                    <div key={m.user_id} className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {m.photo ? (
                          <img src={resolveImageUrl(m.photo)} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-[10px] font-bold text-primary">{(m.name || '?')[0]}</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium truncate">{m.name}</p>
                        {m.role === 'admin' && <p className="text-[10px] text-amber-600 font-medium flex items-center gap-0.5"><Crown className="w-2.5 h-2.5" />Leader</p>}
                      </div>
                    </div>
                  ))}
                  {members.length > 10 && (
                    <p className="text-xs text-muted-foreground text-center pt-1">+{members.length - 10} more members</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Group Events */}
            <Card data-testid="group-events-sidebar">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-sm flex items-center gap-1.5">
                    <Calendar className="w-4 h-4 text-blue-500" /> Events ({groupEvents.length})
                  </h3>
                  {isMember && (
                    <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={() => setShowCreateEvent(true)} data-testid="host-event-btn">
                      <Plus className="w-3 h-3 mr-1" />Host
                    </Button>
                  )}
                </div>
                {groupEvents.length > 0 ? (
                  <div className="space-y-2.5">
                    {groupEvents.slice(0, 5).map(e => (
                      <Link key={e.id} to={`/events/${e.id}`} className="block p-2.5 rounded-lg bg-muted/50 hover:bg-muted transition-colors" data-testid={`group-event-${e.id}`}>
                        <p className="text-xs font-medium truncate">{e.title}</p>
                        <div className="flex items-center gap-2 mt-1 text-[10px] text-muted-foreground">
                          {e.event_date && <span className="flex items-center gap-0.5"><Calendar className="w-2.5 h-2.5" />{new Date(e.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
                          <span className="flex items-center gap-0.5">{e.event_type === 'virtual' ? <Video className="w-2.5 h-2.5" /> : <Building2 className="w-2.5 h-2.5" />}{e.event_type === 'virtual' ? 'Virtual' : 'In-Person'}</span>
                          <span className="flex items-center gap-0.5"><Users className="w-2.5 h-2.5" />{e.rsvp_count || 0}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">{isMember ? 'No events yet. Host the first one!' : 'No events yet.'}</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Create Event Modal */}
        {showCreateEvent && <CreateGroupEventModal groupId={groupId} groupName={group.name} onClose={() => setShowCreateEvent(false)} onCreated={(ev) => { setGroupEvents([ev, ...groupEvents]); setShowCreateEvent(false); toast.success('Event created! Group members have been notified.'); }} />}
      </div>
    </Layout>
  );
}

const CreateGroupEventModal = ({ groupId, groupName, onClose, onCreated }) => {
  const [form, setForm] = useState({ title: '', description: '', event_type: 'virtual', event_date: '', event_time: '', location: '', link: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    setSubmitting(true);
    try {
      const event = await api.createGroupEvent(groupId, form);
      onCreated(event);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create event');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-background rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="create-group-event-modal">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold">Host Event in {groupName}</h2>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">All group members will be invited. The group owner gets auto-RSVP.</p>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="text-xs font-medium mb-1 block">Event Title *</label>
            <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Monthly Networking Mixer" data-testid="event-title-input" />
          </div>
          <div>
            <label className="text-xs font-medium mb-1 block">Description</label>
            <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="What's this event about?" className="w-full px-3 py-2 text-sm rounded-lg border border-input bg-background resize-none" rows={3} data-testid="event-desc-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium mb-1 block">Type</label>
              <select value={form.event_type} onChange={e => setForm({ ...form, event_type: e.target.value })} className="w-full px-3 py-2 text-sm rounded-lg border border-input bg-background" data-testid="event-type-select">
                <option value="virtual">Virtual</option>
                <option value="in_person">In-Person</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block">Date</label>
              <Input type="date" value={form.event_date} onChange={e => setForm({ ...form, event_date: e.target.value })} data-testid="event-date-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium mb-1 block">Time</label>
              <Input type="time" value={form.event_time} onChange={e => setForm({ ...form, event_time: e.target.value })} data-testid="event-time-input" />
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block">{form.event_type === 'virtual' ? 'Meeting Link' : 'Location'}</label>
              <Input
                value={form.event_type === 'virtual' ? form.link : form.location}
                onChange={e => setForm({ ...form, [form.event_type === 'virtual' ? 'link' : 'location']: e.target.value })}
                placeholder={form.event_type === 'virtual' ? 'https://zoom.us/...' : 'Address'}
                data-testid="event-location-input"
              />
            </div>
          </div>
        </div>
        <div className="p-6 border-t flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button className="flex-1" disabled={submitting || !form.title.trim()} onClick={handleSubmit} data-testid="submit-group-event">
            <Calendar className="w-4 h-4 mr-2" />{submitting ? 'Creating...' : 'Create Event'}
          </Button>
        </div>
      </div>
    </div>
  );
};
